/**
 * Every backend route, who on the frontend owns it, and why the few that no
 * screen calls are left alone. `catalog.test.ts` checks this list against the
 * mux in backend/internal/httpapi/server.go, so a route added there and
 * forgotten here fails the build.
 */
export type Consumer =
  | "register"
  | "dashboard:auth"
  | "dashboard:door"
  | "dashboard:board"
  | "dashboard:admin"
  | "dashboard:assistant"
  | "dashboard:superuser"
  | "not-for-frontend";

export interface CatalogEntry {
  method: string;
  /** The path under the API root, `{x}` for a path parameter. */
  path: string;
  /** The mux pattern in server.go this path is registered under. */
  mux: string;
  consumer: Consumer;
  note?: string;
}

const e = (method: string, path: string, mux: string, consumer: Consumer, note?: string): CatalogEntry =>
  note ? { method, path, mux, consumer, note } : { method, path, mux, consumer };

export const CATALOG: readonly CatalogEntry[] = [
  // 37 Registration
  e("POST", "/v1/register", "/v1/register", "register"),
  e("POST", "/v1/register/verify", "/v1/register/verify", "register"),
  e("POST", "/v1/register/resend-otp", "/v1/register/resend-otp", "register"),
  e("GET", "/v1/industries", "/v1/industries", "register", "the form shows it only when the event asks for an industry"),
  e("GET", "/v1/interest-tags", "/v1/interest-tags", "register", "the form shows it only when the event asks for interests"),
  // 39 Event, sign-in, RSVP
  e("GET", "/v1/sessions", "/v1/sessions", "register", "also read by the console's board for the event's name, day and venue"),
  e("POST", "/v1/attendee/login", "/v1/attendee/login", "register"),
  e("POST", "/v1/attendee/verify", "/v1/attendee/verify", "register"),
  e("POST", "/v1/attendee/logout", "/v1/attendee/logout", "register"),
  e("GET", "/v1/attendee/me", "/v1/attendee/me", "register"),
  e("PUT", "/v1/attendee/profile", "/v1/attendee/profile", "register"),
  e("PUT", "/v1/attendee/language", "/v1/attendee/language", "register"),
  e("GET", "/v1/rsvps", "/v1/rsvps", "register", "session step, only when event.rsvp_enabled"),
  e("POST", "/v1/rsvps", "/v1/rsvps", "register", "session step, only when event.rsvp_enabled"),
  e("POST", "/v1/rsvps/cancel", "/v1/rsvps/cancel", "register", "session step, only when event.rsvp_enabled"),
  e("POST", "/v1/rsvps/submit", "/v1/rsvps/submit", "register", "session step, only when event.rsvp_enabled"),
  // 40 Brand and assets
  e("GET", "/v1/brand", "/v1/brand", "register", "both apps"),
  e("GET", "/v1/assets/{key}", "/v1/assets/", "register", "an <img> source from the brand URLs and posters, both apps"),
  e("GET", "/v1/unsubscribe", "/v1/unsubscribe", "not-for-frontend", "the email footer's link; the backend renders its own page"),
  // The on-the-day survey and its merchandise QR (docs/api.md 46, docs/frontend-devs-note.md)
  e("GET", "/v1/survey", "/v1/survey", "register", "/survey page: open or not, before anyone identifies"),
  e("POST", "/v1/survey/lookup", "/v1/survey/lookup", "register", "/survey page: questions, or the voucher if already answered"),
  e("POST", "/v1/survey/submit", "/v1/survey/submit", "register", "/survey page: answers are final; returns the voucher"),
  // The public walk-in form (docs/api.md 47)
  e("GET", "/v1/walk-in", "/v1/walk-in", "register", "/walk-in page: open or not"),
  e("POST", "/v1/walk-in", "/v1/walk-in", "register", "/walk-in page: registers and checks the guest in, no email code"),
  // 38 Staff and the door
  e("POST", "/v1/staff/login", "/v1/staff/login", "dashboard:auth"),
  e("POST", "/v1/staff/logout", "/v1/staff/logout", "dashboard:auth"),
  e("GET", "/v1/staff/me", "/v1/staff/me", "dashboard:auth"),
  e("POST", "/v1/checkins/scan", "/v1/checkins/scan", "dashboard:door"),
  e("POST", "/v1/checkins/sync", "/v1/checkins/sync", "dashboard:door"),
  e("POST", "/v1/checkins/admit", "/v1/checkins/admit", "dashboard:door"),
  e("POST", "/v1/checkins/walk-in", "/v1/checkins/walk-in", "dashboard:door"),
  e("GET", "/v1/attendees/search", "/v1/attendees/search", "dashboard:door"),
  e("POST", "/v1/merch/redeem", "/v1/merch/redeem", "dashboard:door", "the Scan page sends a code starting with merch. here"),
  e("GET", "/v1/frontdesk/state", "/v1/frontdesk/state", "dashboard:board"),
  e("GET", "/v1/frontdesk/stream", "/v1/frontdesk/stream", "dashboard:board", "also the admin pages' live refresh (42.7)"),
  // 42 Admin
  e("GET", "/v1/admin/overview", "/v1/admin/overview", "dashboard:admin"),
  e("GET", "/v1/admin/arrivals", "/v1/admin/arrivals", "dashboard:admin"),
  e("GET", "/v1/admin/registrations", "/v1/admin/registrations", "dashboard:admin"),
  e("GET", "/v1/admin/sessions", "/v1/admin/sessions", "dashboard:admin"),
  e("GET", "/v1/admin/demographics", "/v1/admin/demographics", "dashboard:admin"),
  e("GET", "/v1/admin/attendees", "/v1/admin/attendees", "dashboard:admin", "frontdesk too: the Attendees page and its Check in button"),
  e("GET", "/v1/admin/export", "/v1/admin/export", "dashboard:admin"),
  e("GET", "/v1/admin/survey", "/v1/admin/survey", "dashboard:admin", "survey tile: completed, redeemed, tallies; monitors too"),
  e("GET", "/v1/admin/survey/responses", "/v1/admin/survey/responses", "dashboard:admin", "/survey page: every response, merchandise and answers"),
  e("GET", "/v1/admin/survey/export", "/v1/admin/survey/export", "dashboard:admin", "csv or xlsx; admin and superuser"),
  e("GET", "/v1/admin/survey/questions", "/v1/admin/survey/questions", "dashboard:admin", "/survey page: the editor; admin and superuser"),
  e("PUT", "/v1/admin/survey/questions", "/v1/admin/survey/questions", "dashboard:admin", "/survey page: save, force after a 409"),
  e("DELETE", "/v1/admin/attendees/{id}", "/v1/admin/attendees/", "dashboard:admin", "the attendee dialog's delete for an admin; the superuser's is 45.10"),
  // 44 Kara
  e("GET", "/v1/admin/ai/status", "/v1/admin/ai/status", "dashboard:assistant"),
  e("POST", "/v1/admin/ai/chat", "/v1/admin/ai/chat", "dashboard:assistant"),
  // 45 Superuser
  e("GET", "/v1/superuser/staff", "/v1/superuser/staff", "dashboard:superuser"),
  e("POST", "/v1/superuser/staff", "/v1/superuser/staff", "dashboard:superuser"),
  e("PATCH", "/v1/superuser/staff/{id}", "/v1/superuser/staff/", "dashboard:superuser"),
  e("POST", "/v1/superuser/staff/{id}/password", "/v1/superuser/staff/", "dashboard:superuser"),
  e("POST", "/v1/superuser/staff/{id}/revoke", "/v1/superuser/staff/", "dashboard:superuser"),
  e("GET", "/v1/superuser/event", "/v1/superuser/event", "dashboard:superuser"),
  e("PATCH", "/v1/superuser/event", "/v1/superuser/event", "dashboard:superuser"),
  e("GET", "/v1/superuser/sessions", "/v1/superuser/sessions", "dashboard:superuser"),
  e("POST", "/v1/superuser/sessions", "/v1/superuser/sessions", "dashboard:superuser"),
  e("PATCH", "/v1/superuser/sessions/{id}", "/v1/superuser/sessions/", "dashboard:superuser"),
  e("PUT", "/v1/superuser/sessions/{id}/poster", "/v1/superuser/sessions/", "dashboard:superuser"),
  e("DELETE", "/v1/superuser/sessions/{id}/poster", "/v1/superuser/sessions/", "dashboard:superuser"),
  e("GET", "/v1/superuser/assets", "/v1/superuser/assets", "dashboard:superuser"),
  e("PUT", "/v1/superuser/assets/{key}", "/v1/superuser/assets/", "dashboard:superuser"),
  e("DELETE", "/v1/superuser/assets/{key}", "/v1/superuser/assets/", "dashboard:superuser"),
  e("GET", "/v1/superuser/taxonomies", "/v1/superuser/taxonomies", "dashboard:superuser"),
  e("POST", "/v1/superuser/taxonomies/{kind}", "/v1/superuser/taxonomies/", "dashboard:superuser"),
  e("PATCH", "/v1/superuser/taxonomies/{kind}/{id}", "/v1/superuser/taxonomies/", "dashboard:superuser"),
  e("GET", "/v1/superuser/email/templates", "/v1/superuser/email/templates", "dashboard:superuser"),
  e("GET", "/v1/superuser/email/templates/{key}", "/v1/superuser/email/templates/", "dashboard:superuser"),
  e("PUT", "/v1/superuser/email/templates/{key}", "/v1/superuser/email/templates/", "dashboard:superuser"),
  e("GET", "/v1/superuser/email/templates/{key}/preview", "/v1/superuser/email/templates/", "dashboard:superuser"),
  e("POST", "/v1/superuser/email/templates/{key}/test", "/v1/superuser/email/templates/", "dashboard:superuser"),
  e("GET", "/v1/superuser/email/campaigns", "/v1/superuser/email/campaigns", "dashboard:superuser"),
  e("POST", "/v1/superuser/email/campaigns", "/v1/superuser/email/campaigns", "dashboard:superuser"),
  e("POST", "/v1/superuser/email/campaigns/{id}/{action}", "/v1/superuser/email/campaigns/", "dashboard:superuser", "arm, pause, resume, cancel"),
  e("GET", "/v1/superuser/email/holds", "/v1/superuser/email/holds", "dashboard:superuser"),
  e("POST", "/v1/superuser/email/holds/{id}/release", "/v1/superuser/email/holds/", "dashboard:superuser"),
  e("GET", "/v1/superuser/attendees/{id}", "/v1/superuser/attendees/", "dashboard:superuser"),
  e("PATCH", "/v1/superuser/attendees/{id}", "/v1/superuser/attendees/", "dashboard:superuser"),
  e("DELETE", "/v1/superuser/attendees/{id}", "/v1/superuser/attendees/", "dashboard:superuser"),
  e("PUT", "/v1/superuser/attendees/{id}/profile", "/v1/superuser/attendees/", "dashboard:superuser"),
  e("POST", "/v1/superuser/attendees/{id}/resend-verification", "/v1/superuser/attendees/", "dashboard:superuser"),
  e("POST", "/v1/superuser/attendees/{id}/rsvps", "/v1/superuser/attendees/", "dashboard:superuser"),
  e("DELETE", "/v1/superuser/attendees/{id}/rsvps/{session_id}", "/v1/superuser/attendees/", "dashboard:superuser"),
  e("POST", "/v1/superuser/attendees/{id}/resend-pack", "/v1/superuser/attendees/", "dashboard:superuser"),
  e("DELETE", "/v1/superuser/attendees/{id}/check-in", "/v1/superuser/attendees/", "dashboard:superuser"),
  e("GET", "/v1/superuser/survey/questions", "/v1/superuser/survey/questions", "dashboard:superuser", "the console uses the admin route (46.6)"),
  e("PUT", "/v1/superuser/survey/questions", "/v1/superuser/survey/questions", "dashboard:superuser", "the console uses the admin route (46.6)"),
  // Outside /v1
  e("GET", "/healthz", "/healthz", "not-for-frontend", "the process manager and the proxy (43.1)"),
  e("GET", "/metrics", "/metrics", "not-for-frontend", "Prometheus on the same host (43.2)"),
  e("GET", "/_local/outbox", "/_local/outbox", "not-for-frontend", "local mail catcher; read it like a mailbox, never build a screen on it"),
];
